import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import { useDuetSummary } from '../hooks/queries';
import { useT } from '../i18n';
import { AssistantIcon } from './AssistantIcon';
import { NavIcon, type NavIconName } from './Sidebar';

type Tab = { to: string; label: string; icon: NavIconName; active: (path: string) => boolean };

/**
 * The phone's tab bar: Chat, Nivi (once switched on), History within thumb reach, and More for
 * the drawer with everything else (agents and projects among them). Hidden above the phone
 * width (nivio-ui.css), so the desktop keeps its sidebar, and hidden while typing so the
 * keyboard has the room.
 */
export function MobileTabBar({
  nivi,
  moreOpen,
  onMore,
}: {
  /** Personal agents are on; the Nivi tab still waits until the user switches Nivi on. */
  nivi: boolean;
  moreOpen: boolean;
  onMore: () => void;
}) {
  const t = useT();
  const { pathname } = useLocation();
  const { data } = useDuetSummary(nivi);
  const waiting = data ? data.unseen + data.pendingApprovals : 0;
  // Nivi gets a tab once the user switched it on in Settings.
  const showNivi = nivi && data?.enabled === true;
  const tabs: Tab[] = [
    {
      to: '/',
      label: t('tabChat'),
      icon: 'chat',
      active: (path) => path === '/' || path.startsWith('/c/'),
    },
    ...(showNivi
      ? [
          {
            to: '/nivi',
            label: t('duet'),
            icon: 'duet',
            active: (p) => p.startsWith('/nivi'),
          } as Tab,
        ]
      : []),
    {
      to: '/conversations',
      label: t('tabHistory'),
      icon: 'history',
      active: (p) => p === '/conversations',
    },
  ];
  return (
    <nav className="nv-tabbar" aria-label={t('tabBar')}>
      {tabs.map((tab) => {
        const on = !moreOpen && tab.active(pathname);
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={clsx('nv-tab', on && 'nv-on')}
            aria-current={on ? 'page' : undefined}
          >
            {tab.icon === 'duet' ? <AssistantIcon /> : <NavIcon name={tab.icon} />}
            <span>{tab.label}</span>
            {tab.to === '/nivi' && waiting > 0 && (
              <span
                className="nv-tab-badge"
                role="img"
                aria-label={`${t('duetWaiting')}: ${waiting}`}
              >
                {waiting}
              </span>
            )}
          </Link>
        );
      })}
      <button
        type="button"
        className={clsx('nv-tab', moreOpen && 'nv-on')}
        aria-controls="primary-navigation"
        aria-expanded={moreOpen}
        onClick={onMore}
      >
        <NavIcon name="more" />
        <span>{t('tabMore')}</span>
      </button>
    </nav>
  );
}
